/**
 * Multiple-comparison adjustments for a family of p-values — `p.adjust()` in R.
 *
 * - Bonferroni: min(1, m · p).
 * - Holm (1979): step-down. Sort ascending, multiply the i-th smallest by (m − i + 1),
 *   take the running maximum so the adjusted values stay in order, cap at 1. Holm controls
 *   the same family-wise error rate as Bonferroni and is never less powerful.
 *
 * Both return the adjusted values in the order the p-values came in.
 */

export function bonferroniAdjust(p: readonly number[]): number[] {
  return p.map((value) => Math.min(1, value * p.length));
}

export function holmAdjust(p: readonly number[]): number[] {
  const m = p.length;
  const order = p
    .map((value, index) => ({ value, index }))
    .sort((a, b) => a.value - b.value);
  const adjusted = new Array<number>(m);
  let running = 0;
  for (const [rank, item] of order.entries()) {
    running = Math.max(running, Math.min(1, (m - rank) * item.value));
    adjusted[item.index] = running;
  }
  return adjusted;
}
